import React, { useState, useMemo } from "react";
import { Icon, Button, SearchBox } from "@octano/global-ui";
import { User } from "./types";

export interface LeavesReviewSearchProps {
  users: User[];
  search: string;
  setSearch: (v: string) => void;
  selectedUser: { id: number; full_name: string; rut?: string } | null;
  setSelectedUser: (
    u: { id: number; full_name: string; rut?: string } | null
  ) => void;
  handleSearchReset: () => void;
}

const LeavesReviewSearch: React.FC<LeavesReviewSearchProps> = React.memo(
  ({
    users,
    search,
    setSearch,
    selectedUser,
    setSelectedUser,
    handleSearchReset,
  }) => {
    const trimmedSearch = search.trim();
    const canSearchUsers =
      !selectedUser && (trimmedSearch.length >= 3 || trimmedSearch.length >= 1);

    const [pendingUser, setPendingUser] = useState<{
      id: number;
      full_name: string;
      rut?: string;
    } | null>(null);

    const normalize = (v: string) => v.replace(/[.-]/g, "").toLowerCase();
    const buildFullRut = (u: User): string | undefined => {
      const dv = u.findUserOrds?.data?.[0]?.dv;
      if (dv && dv.length > 0) return `${u.rut}-${dv}`;
      return u.rut || undefined;
    };
    const filteredUsers = useMemo(() => {
      if (!canSearchUsers || !trimmedSearch) return [];
      const termNorm = normalize(trimmedSearch);
      return users.filter((u) => {
        const fullRut = buildFullRut(u);
        return (
          u.full_name.toLowerCase().includes(trimmedSearch.toLowerCase()) ||
          (fullRut && normalize(fullRut).includes(termNorm))
        );
      });
    }, [canSearchUsers, users, trimmedSearch]);

    const handleExecuteSearch = () => {
      if (pendingUser) {
        setSelectedUser(pendingUser);
        return;
      }
      if (filteredUsers.length === 1) {
        const u = filteredUsers[0];
        const fullRut = buildFullRut(u);
        setSelectedUser({ id: u.id, full_name: u.full_name, rut: fullRut });
      }
    };

    return (
      <div className="mb-4 p-3">
        <div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: 12 }}
        >
          <div style={{ minWidth: 320, marginTop: -10, position: "relative" }}>
            <SearchBox
              name="adminUserSearch"
              label="NOMBRE O RUT"
              placeholder="Ingresa nombre o RUT"
              value={selectedUser ? selectedUser.full_name : search}
              onChange={(e) => {
                const v = e.target.value;
                if (selectedUser) setSelectedUser(null);
                setSearch(v);
                setPendingUser(null);
              }}
              clear={() => {
                handleSearchReset();
                setPendingUser(null);
              }}
            />
            {canSearchUsers && filteredUsers.length > 0 && (
              <div
                className="shadow-sm"
                style={{
                  position: "absolute",
                  top: 68,
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  background: "#fff",
                  border: "1px solid #e3e6eb",
                  borderRadius: 4,
                  maxHeight: 260,
                  overflowY: "auto",
                }}
              >
                {filteredUsers.map((u) => {
                  const fullRut = buildFullRut(u);
                  const active = pendingUser?.id === u.id;
                  return (
                    <div
                      key={u.id}
                      className={`d-flex align-items-center justify-content-between ${
                        active ? "bg-primary text-white" : ""
                      }`}
                      style={{
                        cursor: "pointer",
                        padding: "8px 10px",
                        fontSize: 14,
                      }}
                      onClick={() => {
                        setPendingUser({
                          id: u.id,
                          full_name: u.full_name,
                          rut: fullRut,
                        });
                        setSearch(u.full_name);
                      }}
                    >
                      <span
                        className="d-flex align-items-center"
                        style={{ gap: 6 }}
                      >
                        <Icon
                          name="user"
                          size={14}
                          className={active ? "text-white" : "text-dark"}
                        />
                        <span
                          className={active ? "text-white" : "text-dark"}
                          style={{ lineHeight: 1.2 }}
                        >
                          {u.full_name}
                        </span>
                      </span>
                      {fullRut && (
                        <span
                          className={`badge fw-400 ${
                            active ? "bg-light text-dark" : "bg-light text-dark"
                          }`}
                          style={{ fontSize: 11 }}
                        >
                          RUT: {fullRut}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <Button size="sm" text="BUSCAR" onClick={handleExecuteSearch} />
          <Button
            size="sm"
            outlined
            text="LIMPIAR"
            onClick={handleSearchReset}
            disabled={!search && !selectedUser}
          />
        </div>
      </div>
    );
  }
);

LeavesReviewSearch.displayName = "LeavesReviewSearch";

export default LeavesReviewSearch;
